import { STORAGE_KEY } from '../constants/generic';
import { resolveSupportedLocale } from './localeResolver';

// require.context is a webpack construct and does not exist under jest, where
// this inventory serves instead. It is NOT a second source of truth: the
// locales.sync.test.js suite compares it against the real directory contents
// and fails on any drift.
export const JEST_FALLBACK_LOCALES = [
  'cs',
  'da',
  'de',
  'en',
  'es',
  'fr',
  'it',
  'nl',
  'pl',
  'pt',
  'pt_BR',
  'sk',
  'sv'
];

const localeFiles =
  typeof require.context === 'function'
    ? require.context('./', false, /\.json$/)
    : null;

// The single source of truth for the locales the app ships is this directory:
// every JSON file here IS a supported locale, so the list is read off the files
// instead of being written out a second time by hand (jest aside, see above).
export const SUPPORTED_LOCALES = localeFiles
  ? localeFiles
      .keys()
      .map((fileName) => fileName.replace(/^\.\//, '').replace(/\.json$/, ''))
  : JEST_FALLBACK_LOCALES;

// What the app speaks before any brand preference arrives (App.handleBrandLanguage
// may switch later) - and the honest fallback for unsupported browser languages.
export const DEFAULT_LOCALE = 'en';

export function resolveAppLocale(candidates) {
  return resolveSupportedLocale(candidates, SUPPORTED_LOCALES);
}

// safe() lives in utils, which sits inside the utils <-> store import cycle.
// This module must stay cycle-free - the i18n reducer calls getDefaultLocale
// during combineReducers, before that cycle settles - so it carries its own
// tiny guard instead.
function quietly(read) {
  try {
    return read();
  } catch (e) {
    return undefined;
  }
}

export function getSavedLanguage() {
  return quietly(() => window.localStorage.getItem(STORAGE_KEY.LOCALE));
}

// Always a locale the app actually ships: the saved value and the browser
// language are both candidates (either can be unsupported or plain garbage),
// resolved against this directory's contents - never a raw unvalidated string.
export function getDefaultLocale() {
  return (
    resolveAppLocale([getSavedLanguage(), quietly(() => navigator.language)]) ||
    DEFAULT_LOCALE
  );
}

// The only place locale messages are loaded from: the locale has been validated
// against SUPPORTED_LOCALES, so the lookup cannot throw - unlike the old
// require('../../i18n/' + locale), which white-screened the app on any browser
// language we do not ship (TODO §16). Under jest a plain dynamic require serves
// (CJS supports it; the validated name keeps it safe there too).
export function getLocaleMessages(locale) {
  if (localeFiles) return localeFiles('./' + locale + '.json');
  return require('./' + locale + '.json');
}
